/**
 * §P.1 · El listado de alumnos. **La pantalla madre.**
 *
 * `BFF_BO.md` §7.1. Tres cosas que esta pantalla hace a propósito:
 *
 * **Pagina por cursor, nunca por número de página.** Los listados cambian
 * mientras se leen: el administrativo que da de baja a alguien de la página 2
 * desplaza la 3, y con números se salta una fila sin que nadie lo vea. Además no
 * hay total y no lo va a haber: contar 4.000 alumnos en cada página cuesta más que
 * devolverlos.
 *
 * **El entrenador ve solo los alumnos de sus grupos, y eso lo resuelve la
 * consulta** —no la interfaz ocultando filas—. Aquí no hay ni un filtro por rol:
 * lo que llega es lo que puede ver.
 *
 * **Escribir es de `owner` y `admin`.** Al entrenador no se le enseña el botón de
 * alta, porque un menú lleno de opciones que dan 403 es una aplicación que parece
 * rota. Ocultar es cortesía: el servidor lo impide igual.
 */
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';
import {
  anterior,
  cursorActual,
  esLaPrimera,
  numeroDePagina,
  siguiente,
  PRIMERA,
  type PilaDeCursores,
} from '@/shared/ui/paginas';
import { EstadoDeAlumno } from '@/club/alumnos/EstadoDeAlumno';
import { FormularioDeAlumno } from '@/club/alumnos/FormularioDeAlumno';
import { ImportarAlumnos } from '@/club/alumnos/importacion/ImportarAlumnos';
import { AccionesEnBloque } from '@/club/alumnos/varios/AccionesEnBloque';
import {
  alternar,
  alternarPagina,
  conTodos,
  NINGUNO,
  soloLosSaltados,
  todosMarcados,
  type Seleccion,
} from '@/club/alumnos/varios/seleccion';
import {
  nombreCompleto,
  type EstadoAlumno,
  type Grupo,
  type PaginaDeAlumnos,
} from '@/club/alumnos/tipos';

/**
 * Los filtros. Tres, y son los que hay.
 *
 * `endpoints.md` §P.1 prometía cinco —añadía «moroso» y «sin consentimiento»— y se
 * decidió el 16-09-2026 **no añadirlos**: los morosos se ven en
 * `GET /invoices?unpaid=true`, que da más contexto que un filtro sobre este
 * listado. Los dos documentos ya lo dicen así, así que esto no es una carencia:
 * es el contrato.
 */
interface Filtros {
  readonly q: string;
  readonly status: EstadoAlumno | '';
  readonly group_id: string;
  /**
   * Nacidos desde / hasta este año (03-11-2026): «coges todos los niños de este
   * año, el otro y el siguiente». Se escribe a mano y solo se manda con cuatro
   * cifras: «20» a medio escribir no es un año.
   */
  readonly desde: string;
  readonly hasta: string;
}

const SIN_FILTROS: Filtros = {
  q: '',
  status: 'active',
  group_id: '',
  desde: '',
  hasta: '',
};

const anio = (v: string) => (/^\d{4}$/.test(v) ? Number(v) : undefined);

/** La consulta del listado con estos filtros, para una página o para todas. */
const consultaDe = (f: Filtros) => {
  const desde = anio(f.desde);
  const hasta = anio(f.hasta);
  return {
    ...(f.q ? { q: f.q } : {}),
    ...(f.status ? { status: f.status } : {}),
    ...(f.group_id ? { group_id: f.group_id } : {}),
    ...(desde !== undefined ? { birth_year_from: desde } : {}),
    ...(hasta !== undefined ? { birth_year_to: hasta } : {}),
  };
};

const ESTADOS: readonly { valor: EstadoAlumno | ''; etiqueta: string }[] = [
  { valor: 'active', etiqueta: 'Activos' },
  { valor: 'trial', etiqueta: 'En prueba' },
  { valor: 'paused', etiqueta: 'En pausa' },
  { valor: 'left', etiqueta: 'Dados de baja' },
  { valor: '', etiqueta: 'Todos' },
];

const hayFiltro = (f: Filtros) =>
  f.q !== '' ||
  f.group_id !== '' ||
  f.status !== 'active' ||
  f.desde !== '' ||
  f.hasta !== '';

export function ListaDeAlumnos({
  club,
  puedeEscribir,
  suspendido,
  alAbrir,
  empezarDandoDeAlta = false,
}: {
  club: string;
  puedeEscribir: boolean;
  suspendido: boolean;
  alAbrir: (alumnoId: string) => void;
  /** Con el formulario de alta ya abierto: «Añadir un alumno» de Inicio. */
  empezarDandoDeAlta?: boolean;
}) {
  const [dandoDeAlta, setDandoDeAlta] = useState(empezarDandoDeAlta);
  const [importando, setImportando] = useState(false);
  const [filtros, setFiltros] = useState<Filtros>(SIN_FILTROS);
  // Un cursor solo apunta hacia delante, así que para poder volver hay que
  // acordarse del camino. Ver `shared/ui/paginas`.
  const [cursores, setCursores] = useState<PilaDeCursores>(PRIMERA);
  const cursor = cursorActual(cursores);
  // Varios a la vez: quien puede editar alumnos, y no con el club suspendido.
  const puedeElegir = puedeEscribir && !suspendido;
  const [seleccion, setSeleccion] = useState<Seleccion>(NINGUNO);
  const [eligiendoTodos, setEligiendoTodos] = useState(false);

  const cambiar = (parcial: Partial<Filtros>) => {
    setFiltros((actual) => ({ ...actual, ...parcial }));
    // Cambiar un filtro vuelve a la primera página. Sin esto, el cursor de la
    // página 3 de «activos» se aplicaría a «dados de baja» y saldría vacío.
    setCursores(PRIMERA);
  };

  const grupos = useQuery({
    queryKey: ['grupos', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).groups.get({
        query: {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Grupo[];
    },
  });

  const alumnos = useQuery({
    queryKey: ['alumnos', club, filtros, cursor],
    // Mantener la página anterior mientras llega la siguiente evita que la tabla
    // pegue un salto a esqueleto en cada «Siguiente».
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).students.get({
        query: {
          limit: 25,
          ...(cursor ? { cursor } : {}),
          ...consultaDe(filtros),
        },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PaginaDeAlumnos;
    },
  });

  /**
   * «Elegir también los de las demás páginas»: recorre el listado con el mismo
   * filtro, de 200 en 200, y los marca. Con tope de 1.000, que es más que cualquier
   * club, para que un filtro vacío no se convierta en un bucle largo.
   */
  const elegirTodos = async () => {
    setEligiendoTodos(true);
    try {
      let siguienteCursor: string | null = null;
      const todos: { id: string; nombre: string }[] = [];
      for (let vuelta = 0; vuelta < 5; vuelta += 1) {
        const { data, error } = await api.v1.clubs({ club }).students.get({
          query: {
            limit: 200,
            ...(siguienteCursor ? { cursor: siguienteCursor } : {}),
            ...consultaDe(filtros),
          },
        });
        if (error) throw new ErrorDeApi(aProblema(error));
        const pagina = data as unknown as PaginaDeAlumnos;
        todos.push(
          ...pagina.data.map((a) => ({ id: a.id, nombre: nombreCompleto(a) }))
        );
        if (!pagina.has_more || !pagina.next_cursor) break;
        siguienteCursor = pagina.next_cursor;
      }
      setSeleccion((s) => conTodos(s, todos));
    } finally {
      setEligiendoTodos(false);
    }
  };

  if (importando) {
    return (
      <ImportarAlumnos
        club={club}
        alTerminar={() => setImportando(false)}
        alCancelar={() => setImportando(false)}
      />
    );
  }

  if (dandoDeAlta) {
    return (
      <FormularioDeAlumno
        club={club}
        alumno={null}
        etag={null}
        suspendido={suspendido}
        alGuardar={(id) => {
          setDandoDeAlta(false);
          // Directo a su ficha: quien acaba de dar de alta a alguien lo siguiente
          // que quiere es ponerle el tutor.
          alAbrir(id);
        }}
        alCancelar={() => setDandoDeAlta(false)}
      />
    );
  }

  return (
    <section>
      <header className="titulo-fila">
        <h1>Alumnos</h1>
        {puedeEscribir && (
          <div className="titulo-acciones">
            <button
              type="button"
              className="btn-plano"
              onClick={() => setImportando(true)}
            >
              Importar Excel
            </button>
            <button type="button" onClick={() => setDandoDeAlta(true)}>
              Añadir alumno
            </button>
          </div>
        )}
      </header>

      <div className="filtros">
        <label>
          <span className="sr">Buscar</span>
          <input
            type="search"
            placeholder="Nombre, apellido o número de socio"
            value={filtros.q}
            onChange={(e) => cambiar({ q: e.target.value })}
          />
        </label>

        <label>
          <span className="sr">Estado</span>
          <select
            value={filtros.status}
            onChange={(e) =>
              cambiar({ status: e.target.value as EstadoAlumno | '' })
            }
          >
            {ESTADOS.map((e) => (
              <option key={e.valor} value={e.valor}>
                {e.etiqueta}
              </option>
            ))}
          </select>
        </label>

        <span className="filtro-anios">
          <label>
            Nacidos de
            <input
              inputMode="numeric"
              maxLength={4}
              placeholder="2016"
              aria-label="Nacidos desde el año"
              value={filtros.desde}
              onChange={(e) =>
                cambiar({ desde: e.target.value.replace(/\D/g, '') })
              }
            />
          </label>
          <label>
            a
            <input
              inputMode="numeric"
              maxLength={4}
              placeholder="2018"
              aria-label="Nacidos hasta el año"
              value={filtros.hasta}
              onChange={(e) =>
                cambiar({ hasta: e.target.value.replace(/\D/g, '') })
              }
            />
          </label>
        </span>

        <label>
          <span className="sr">Grupo</span>
          <select
            value={filtros.group_id}
            onChange={(e) => cambiar({ group_id: e.target.value })}
            disabled={!grupos.data}
          >
            <option value="">Todos los grupos</option>
            {(grupos.data ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
                {g.venue_name ? ` · ${g.venue_name}` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      {puedeElegir && (
        <AccionesEnBloque
          club={club}
          seleccion={seleccion}
          grupos={grupos.data ?? []}
          hayMasPaginas={Boolean(alumnos.data?.has_more)}
          eligiendoTodos={eligiendoTodos}
          alElegirTodos={() => void elegirTodos()}
          alQuitar={() => setSeleccion(NINGUNO)}
          alTerminar={(r) => setSeleccion((s) => soloLosSaltados(s, r.skipped))}
        />
      )}

      {alumnos.isPending && <Cargando filas={6} etiqueta="Cargando alumnos" />}

      {alumnos.isError && (
        <PanelError
          problema={aProblema(alumnos.error)}
          reintentar={() => void alumnos.refetch()}
        />
      )}

      {alumnos.data?.data.length === 0 &&
        (hayFiltro(filtros) ? (
          <Vacio
            titulo="Ningún alumno encaja con eso"
            explicacion="Prueba a quitar algún filtro o a buscar por el número de socio."
            accion={
              <button type="button" onClick={() => cambiar(SIN_FILTROS)}>
                Quitar los filtros
              </button>
            }
          />
        ) : (
          // Vacío de verdad: qué es esta pantalla y **la acción que la llena**.
          <Vacio
            titulo="Todavía no hay alumnos"
            explicacion="Aquí va tu gente. Lo más rápido es traer el Excel que ya tienes: se previsualiza antes de importar nada, así que no hay sorpresas."
            accion={
              puedeEscribir ? (
                <>
                  <button type="button" onClick={() => setImportando(true)}>
                    Importar Excel
                  </button>
                  <button type="button" className="btn-plano">
                    Añadir uno a mano
                  </button>
                </>
              ) : undefined
            }
          />
        ))}

      {alumnos.data && alumnos.data.data.length > 0 && (
        <>
          {/* En el móvil, una tarjeta por alumno (`tabla-tarjetas`, tema.css). */}
          <div className="tabla-marco">
            <table className="tabla tabla-tarjetas">
              <thead>
                <tr>
                  {puedeElegir && (
                    <th className="celda-casilla">
                      <input
                        type="checkbox"
                        aria-label="Elegir a todos los de esta página"
                        checked={todosMarcados(
                          seleccion,
                          alumnos.data.data.map((a) => a.id)
                        )}
                        onChange={() =>
                          setSeleccion((s) =>
                            alternarPagina(
                              s,
                              (alumnos.data?.data ?? []).map((a) => ({
                                id: a.id,
                                nombre: nombreCompleto(a),
                              }))
                            )
                          )
                        }
                      />
                    </th>
                  )}
                  <th>Alumno</th>
                  <th>Socio</th>
                  <th className="num">Edad</th>
                  <th>Estado</th>
                  <th>Alta</th>
                </tr>
              </thead>
              <tbody>
                {alumnos.data.data.map((a) => (
                  <tr
                    key={a.id}
                    className={`fila-pulsable${seleccion.has(a.id) ? ' fila-elegida' : ''}`}
                    onClick={() => alAbrir(a.id)}
                  >
                    {puedeElegir && (
                      <td
                        className="celda-casilla"
                        data-label="Elegir"
                        // La fila entera abre la ficha; la casilla no.
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          aria-label={`Elegir a ${nombreCompleto(a)}`}
                          checked={seleccion.has(a.id)}
                          onChange={() =>
                            setSeleccion((s) =>
                              alternar(s, a.id, nombreCompleto(a))
                            )
                          }
                        />
                      </td>
                    )}
                    <td className="celda-titulo">
                      {/* Un botón de verdad y no solo la fila: con teclado, una
                          fila con `onClick` no existe. */}
                      <button type="button" className="enlace-fila">
                        {nombreCompleto(a)}
                      </button>
                    </td>
                    <td className="tenue" data-label="Socio">
                      {a.code ?? '—'}
                    </td>
                    <td className="num" data-label="Edad">
                      {a.age ?? '—'}
                    </td>
                    <td data-label="Estado">
                      <EstadoDeAlumno estado={a.status} />
                    </td>
                    <td className="tenue" data-label="Alta">
                      {fecha(a.joined_on)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <nav className="paginacion">
            <button
              type="button"
              className="btn-plano"
              disabled={esLaPrimera(cursores) || alumnos.isFetching}
              onClick={() => setCursores(anterior)}
            >
              Anterior
            </button>
            <span className="tenue">
              {/* Sin «página 3 de 12»: no hay total, y no es un descuido. */}
              {esLaPrimera(cursores)
                ? 'Primeros 25'
                : `Página ${numeroDePagina(cursores)}`}
            </span>
            <button
              type="button"
              className="btn-plano"
              disabled={!alumnos.data.has_more || alumnos.isFetching}
              onClick={() =>
                setCursores((c) =>
                  siguiente(c, alumnos.data?.next_cursor ?? null)
                )
              }
            >
              Siguiente
            </button>
          </nav>
        </>
      )}
    </section>
  );
}
